import { defineStore } from "pinia";
import { axiosInstance } from "@/plugins/https";
import { useSnackbarStore } from "@/_store/snackbar.module";
import { i18n } from "@/plugins/i18n";
import { EdrRemediationMode } from "@/constants/edr";
import type { DeviceLabel } from "@/_store/devices-settings.module";
import api from "@/_helpers/api";
import { StoreModule } from "@/constants/store.ts";

export interface EdrSettings {
  enableEdrEnforcement: boolean;
  remediationMode: EdrRemediationMode;
  labels: DeviceLabel[];
  asrRulesEnabled: boolean;
  asrRulesLabels: DeviceLabel[];
}

export interface EdrSettingsState {
  settings: EdrSettings;
  loading: boolean;
}

const defaultEdrSettingsState: EdrSettingsState = {
  settings: {
    enableEdrEnforcement: true,
    remediationMode: EdrRemediationMode.DETECT_ONLY,
    labels: [],
    asrRulesEnabled: false,
    asrRulesLabels: [],
  },
  loading: false,
};

export const useEdrSettingsStore = defineStore(StoreModule.EDR_SETTINGS, {
  state: (): EdrSettingsState => ({ ...defaultEdrSettingsState }),
  actions: {
    async getEdrSettings() {
      this.loading = true;

      try {
        const request = api.edrSettings;
        const { data } = await axiosInstance.request<EdrSettings>(request);
        this.settings = data;
      } catch (error) {
        console.error("Failed to fetch EDR settings:", error);
      } finally {
        this.loading = false;
      }
    },

    async updateEdrSettings(settings: EdrSettings) {
      this.loading = true;
      const request = {
        ...api.edrSettings,
        method: "PUT",
        data: settings,
      };

      try {
        const { data } = await axiosInstance.request<EdrSettings>(request);
        this.settings = data;
        useSnackbarStore().addGenericSuccess(i18n.global.t("edr.settings.success.updated"));
      } catch (error) {
        console.error("Failed to update EDR settings:", error);
        throw error;
      } finally {
        this.loading = false;
      }
    },
  },
});
